import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { blogData } from '../Blog/BlogData'; 
import { Calendar, ChevronRight, ChevronLeft } from 'lucide-react';

const Blogs = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const blogsPerPage = 6;

  // Sort blogs by date (latest first)
  const sortedBlogs = [...blogData].sort(
    (a, b) => new Date(b.date) - new Date(a.date)
  );

  // Pagination logic
  const indexOfLastBlog = currentPage * blogsPerPage;
  const indexOfFirstBlog = indexOfLastBlog - blogsPerPage;
  const currentBlogs = sortedBlogs.slice(indexOfFirstBlog, indexOfLastBlog);
  const totalPages = Math.ceil(sortedBlogs.length / blogsPerPage);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);

  return (
    <div className="bg-[#fdfaf6] pt-40 pb-20 min-h-screen font-serif text-[#1a1a1a]">
      <div className="max-w-7xl mx-auto px-6">

        {/* Header */}
        <div className="text-center mb-20">
          <span className="text-[#8b5e3c] uppercase tracking-widest text-sm font-bold">Legal Library</span>
          <h2 className="text-4xl md:text-5xl font-bold mt-4 italic">Latest Legal Insights</h2>
          <div className="w-28 h-[2px] bg-[#8b5e3c] mx-auto mt-6"></div>
        </div>

        {/* Blog Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
          {currentBlogs.map((blog) => (
            <Link 
              to={`/blogs/${blog.id}`} 
              key={blog.id} 
              className="group bg-white border border-gray-200 shadow-md hover:shadow-lg transition-shadow duration-500 flex flex-col rounded-lg overflow-hidden"
            >
              <div className="relative h-64 overflow-hidden">
                <img 
                  src={blog.image} 
                  alt={blog.title} 
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" 
                />
                <div className="absolute top-4 left-4 bg-[#1a1a1a] text-[#f0e6d2] text-[10px] font-semibold px-3 py-1 tracking-wider uppercase rounded">
                  {blog.category}
                </div>
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <div className="flex items-center gap-2 text-gray-500 text-[10px] mb-3 uppercase tracking-wide">
                  <Calendar size={12} className="text-[#8b5e3c]" /> {blog.date}
                </div>
                <h3 className="text-[#1a1a1a] text-xl font-semibold mb-4 leading-snug line-clamp-2">{blog.title}</h3>
                <p className="text-gray-600 text-sm mb-6 line-clamp-3">{blog.excerpt}</p>
                <div className="flex items-center gap-2 text-[#8b5e3c] text-[11px] font-semibold uppercase tracking-wide border-t border-gray-100 pt-4">
                  Read Case Study <ChevronRight size={14} />
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-24 flex justify-center items-center gap-3">
            <button 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`px-4 py-2 border border-[#1a1a1a] rounded transition-colors ${currentPage === 1 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-[#1a1a1a] hover:text-[#fdfaf6]'}`}
            >
              <ChevronLeft size={18} />
            </button>

            {[...Array(totalPages)].map((_, index) => (
              <button
                key={index + 1}
                onClick={() => setCurrentPage(index + 1)}
                className={`px-4 py-2 border rounded font-medium transition-all ${
                  currentPage === index + 1 
                  ? 'bg-[#1a1a1a] text-[#fdfaf6] border-[#1a1a1a]' 
                  : 'bg-white text-[#1a1a1a] border-gray-300 hover:bg-[#fdfaf6] hover:border-[#8b5e3c]'
                }`}
              >
                {index + 1}
              </button>
            ))}

            <button 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className={`px-4 py-2 border border-[#1a1a1a] rounded transition-colors ${currentPage === totalPages ? 'opacity-30 cursor-not-allowed' : 'hover:bg-[#1a1a1a] hover:text-[#fdfaf6]'}`}
            >
              <ChevronRight size={18} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Blogs;